/**
 * Overview tab — KPI cards + AI commentary + funnel + daily timeline.
 * Extracted from AnalyticsCalls.jsx (iter29 refactor). Iso-behaviour.
 */
import { ArrowRight, Clock, Flame, Loader2, Phone, PhoneOff, Target, TrendingUp } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";

import { api } from "../../lib/api";
import { AiCommentaryBlock } from "./AiCommentaryBlock";
import { KpiCard } from "./shared";

export function OverviewTab({ filters }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        api.get("/analytics/calls/overview", {
            params: {
                from: filters.from,
                to: filters.to,
                offer_id: filters.offer_id || undefined,
                recruiter_id: filters.recruiter_id || undefined,
                source: filters.source || undefined,
            },
        }).then(({ data }) => {
            if (!cancelled) setData(data);
        }).catch(() => {
            if (!cancelled) toast.error("Impossible de charger la vue d'ensemble");
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, [filters]);

    if (loading || !data) {
        return <div className="text-slate-400 text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
    }

    const { kpis, trend_vs_prev, funnel, timeline } = data;
    // iter88 · Breakdowns absolus pour chaque KPI (CEO : "je veux le nombre, pas juste le %").
    const d = kpis.denominators || {};
    const bk = (num, den, label) => (den ? `${num} / ${den} ${label}` : `— / — ${label}`);

    return (
        <div className="space-y-5">
            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                <KpiCard testid="kpi-calls-total" label="Appels émis" value={kpis.calls_total} delta={trend_vs_prev.calls_delta_pct} tone="orange" icon={Phone}
                    breakdown={kpis.unique_candidates ? `${kpis.unique_candidates} candidat${kpis.unique_candidates > 1 ? "s" : ""} distinct${kpis.unique_candidates > 1 ? "s" : ""}` : null} />
                <KpiCard testid="kpi-reach-rate" label="Taux décroché" value={kpis.reach_rate} unit="%" delta={trend_vs_prev.reach_rate_delta_pct} tone="emerald" icon={PhoneOff}
                    breakdown={bk(kpis.reached_count ?? 0, d.reached_denom ?? kpis.calls_total, "décrochés")} />
                <KpiCard testid="kpi-completed" label="Entretiens complétés" value={kpis.complete_count} delta={trend_vs_prev.complete_count_delta_pct} tone="indigo" icon={Clock}
                    breakdown={d.complete_denom ? `sur ${d.complete_denom} décroché${d.complete_denom > 1 ? "s" : ""}` : null} />
                <KpiCard testid="kpi-qualify-rate" label="Taux qualification" value={kpis.qualify_rate} unit="%" delta={trend_vs_prev.qualify_rate_delta_pct} tone="orange" icon={Flame}
                    breakdown={bk(kpis.qualified_count ?? 0, d.qualify_denom ?? 0, "qualifiés")} />
                <KpiCard testid="kpi-closing-rate" label="Closing global" value={kpis.closing_rate} unit="%" delta={trend_vs_prev.closing_rate_delta_pct} tone="emerald" icon={Target}
                    breakdown={bk(kpis.closed_candidates_count ?? kpis.qualified_count ?? 0, d.closing_denom ?? kpis.unique_candidates, "candidats fermés")} />
                <KpiCard testid="kpi-attempts" label="Tentatives / cand." value={kpis.attempts_per_candidate} delta={trend_vs_prev.attempts_per_candidate_delta_pct} tone="slate" icon={TrendingUp}
                    breakdown={kpis.calls_total && kpis.unique_candidates ? `${kpis.calls_total} appels · ${kpis.unique_candidates} cand.` : null} />
            </div>

            {/* AI Commentary */}
            <AiCommentaryBlock scope="overview" filters={filters} testid="ai-commentary-overview" />

            {/* Funnel */}
            <div className="bg-white rounded-2xl border-2 border-slate-100 p-5">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3">
                    Funnel de conversion
                </div>
                <div className="flex items-stretch gap-2 overflow-x-auto pb-2" data-testid="funnel">
                    {funnel.map((step, i) => (
                        <div key={step.key} className="flex items-center gap-2 flex-shrink-0">
                            <div className={`min-w-[9rem] rounded-2xl p-3 border-2 ${i === funnel.length - 1 ? "border-shariti-orange bg-shariti-orange-soft" : "border-slate-200 bg-white"}`}>
                                <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">
                                    {step.label}
                                </div>
                                <div className="text-2xl font-black text-shariti-black mt-1">
                                    {step.count}
                                </div>
                                {i > 0 && (
                                    <div className="text-[10px] font-bold text-slate-500 mt-0.5">
                                        {step.passage_rate}% passage
                                    </div>
                                )}
                            </div>
                            {i < funnel.length - 1 && <ArrowRight className="w-4 h-4 text-slate-300 shrink-0" />}
                        </div>
                    ))}
                </div>
            </div>

            {/* Timeline */}
            <div className="bg-white rounded-2xl border-2 border-slate-100 p-5">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3">
                    Évolution quotidienne
                </div>
                {timeline.length === 0 ? (
                    <div className="text-slate-400 text-sm italic text-center py-8">Pas d&apos;appels dans la période sélectionnée.</div>
                ) : (
                    <SimpleTimeline data={timeline} />
                )}
            </div>
        </div>
    );
}

function SimpleTimeline({ data }) {
    // Full-featured Recharts composed chart with Y-axis, tooltip and legend.
    // We show call VOLUME as bars (left axis) and REACH RATE % as a line
    // (right axis) so peaks and dips are readable at a glance.
    const chartData = useMemo(() => (data || []).map((d) => ({
        day: d.day.slice(5),
        fullDay: d.day,
        Volume: d.volume,
        "Taux décroché (%)": d.reach_rate,
    })), [data]);

    return (
        <div className="w-full h-64" data-testid="timeline">
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 8, right: 24, left: 0, bottom: 8 }} barCategoryGap="20%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                    <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#94A3B8", fontWeight: 700 }} axisLine={{ stroke: "#E5E7EB" }} tickLine={false} interval="preserveStartEnd" minTickGap={20} />
                    <YAxis yAxisId="volume" tick={{ fontSize: 10, fill: "#94A3B8", fontWeight: 700 }} axisLine={{ stroke: "#E5E7EB" }} tickLine={false} allowDecimals={false} label={{ value: "Appels", angle: -90, position: "insideLeft", offset: 12, fontSize: 10, fill: "#94A3B8", fontWeight: 700 }} />
                    <YAxis yAxisId="rate" orientation="right" domain={[0, 100]} tick={{ fontSize: 10, fill: "#94A3B8", fontWeight: 700 }} axisLine={{ stroke: "#E5E7EB" }} tickLine={false} tickFormatter={(v) => `${v}%`} />
                    <Tooltip cursor={{ fill: "rgba(242,108,54,0.06)" }} contentStyle={{ border: "2px solid #F1F5F9", borderRadius: 12, fontSize: 12, fontWeight: 600, padding: 10 }} labelFormatter={(_v, p) => p?.[0]?.payload?.fullDay || _v} />
                    <Legend verticalAlign="top" height={28} iconType="circle" wrapperStyle={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", color: "#64748B" }} />
                    <Bar yAxisId="volume" dataKey="Volume" fill="#F26C36" radius={[4, 4, 0, 0]} maxBarSize={40} />
                    <Line yAxisId="rate" type="monotone" dataKey="Taux décroché (%)" stroke="#10B981" strokeWidth={2.5} dot={{ r: 3, fill: "#10B981" }} activeDot={{ r: 5 }} />
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    );
}
